<template>
                <div v-if="currentView === 'contacts'">
                    <div class="page-header"><div><div class="page-title">聯絡人管理</div><div class="page-subtitle">管理企業與個人聯絡人資料</div></div></div>

                    <div class="card p-6 mb-6">
                        <div class="flex flex-wrap gap-2 items-center">
                            <button @click="contactsTab = 'contacts'"
                                :class="contactsTab === 'contacts' ? 'active' : ''"
                        class="tab-item">
                                聯絡人
                            </button>
                            <button @click="contactsTab = 'groups'; fetchContactGroups()"
                                :class="contactsTab === 'groups' ? 'active' : ''"
                        class="tab-item">
                                聯絡人群組
                            </button>
                            <div class="ml-auto flex gap-2">
                                <template v-if="contactsTab === 'contacts'">
                                    <label class="btn btn-outline btn-sm cursor-pointer" title="匯入 CSV 聯絡人">
                                        <input type="file" accept=".csv,text/csv" class="hidden" @change="handleContactCsvUpload">
                                        ⬆ 匯入 CSV
                                    </label>
                                    <button @click="openCreateContact()" class="btn btn-primary text-sm font-bold">新增聯絡人</button>
                                </template>
                                <button v-else-if="contactsTab === 'groups' && (isSuperAdmin || isDomainAdmin)" @click="openCreateContactGroup()"
                                    class="btn btn-primary text-sm font-bold">
                                    新增群組
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="contactsTab === 'contacts'" class="card p-6">
                        <div class="flex flex-wrap gap-3 items-end mb-4">
                            <div class="flex-1 min-w-[220px]">
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">搜尋（姓名/Email）</label>
                                <input v-model="contactFilters.search" @keyup.enter="fetchContacts()"
                                    class="input" placeholder="例如: alice 或 alice@example.com">
                            </div>
                            <div class="w-48">
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">標籤</label>
                                <select v-model="contactFilters.tag" class="input">
                                    <option value="">全部</option>
                                    <option v-for="t in contactTags" :key="t" :value="t">{{ t }}</option>
                                </select>
                            </div>
                            <div class="w-56">
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">群組</label>
                                <select v-model="contactFilters.group_id" class="input">
                                    <option :value="null">全部</option>
                                    <option v-for="(g, idx) in safeArr(contactGroups)" :key="keyById(g, 'cg', idx)" :value="g.id">{{ g.name }}</option>
                                </select>
                            </div>
                            <div class="w-44">
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">排序</label>
                                <select v-model="contactFilters.sort_by" class="input">
                                    <option value="name">姓名</option>
                                    <option value="lead_score">Lead Score</option>
                                    <option value="last_interaction_at">最後互動</option>
                                    <option value="created_at">建立時間</option>
                                </select>
                            </div>
                            <div class="w-32">
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">方向</label>
                                <select v-model="contactFilters.sort_dir" class="input">
                                    <option value="asc">升冪</option>
                                    <option value="desc">降冪</option>
                                </select>
                            </div>
                            <button @click="fetchContacts()"
                                class="btn btn-primary text-sm font-bold">
                                查詢
                            </button>
                            <button @click="resetContactFilters()"
                                class="btn btn-secondary">
                                重設
                            </button>
                        </div>

                        <div v-if="contactsLoading" class="p-6 text-center" style="color:var(--gray-500);">
                            <span class="animate-spin mr-2">⟳</span> 載入中...
                        </div>

                        <div v-else class="overflow-x-auto">
                            <table class="data-table">
                                <thead>
                                    <tr class="border-b" style="background:var(--gray-50);">
                                        <th class="p-2 w-20">類型</th>
                                        <th class="p-2">姓名</th>
                                        <th class="p-2">Email</th>
                                        <th class="p-2">電話</th>
                                        <th class="p-2">標籤</th>
                                        <th class="p-2">群組</th>
                                        <th class="p-2 w-24">Lead</th>
                                        <th class="p-2 w-44">最後互動</th>
                                        <th class="p-2" v-if="isSuperAdmin || isDomainAdmin">擁有者</th>
                                        <th class="p-2 w-44">建立時間</th>
                                        <th class="p-2 w-28">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="contacts.length === 0">
                                        <td :colspan="(isSuperAdmin || isDomainAdmin) ? 11 : 10" class="p-8 text-center" style="color:var(--gray-500);">
                                            尚無聯絡人
                                        </td>
                                    </tr>
                                    <tr v-for="(c, idx) in safeArr(contacts)" :key="keyById(c, 'contact', idx)" class="border-b">
                                        <td class="p-2">
                                            <span v-if="!c.user_id" class="btn btn-secondary btn-xs">共享</span>
                                            <span v-else class="badge" style="background:var(--brand-100);color:var(--brand-700);">私有</span>
                                        </td>
                                        <td class="p-2 font-bold">{{ c.name }}</td>
                                        <td class="p-2 font-mono text-sm">{{ c.email }}</td>
                                        <td class="p-2">{{ c.phone || '-' }}</td>
                                        <td class="p-2 max-w-[160px]">
                                            <div class="flex flex-wrap gap-1" v-if="c.tags">
                                                <span v-for="t in c.tags.split(',').map(x=>x.trim()).filter(Boolean)" :key="t"
                                                    style="display:inline-block;background:var(--brand-50);color:var(--brand-700);border-radius:999px;padding:1px 8px;font-size:0.7rem;font-weight:600;">{{ t }}</span>
                                            </div>
                                            <span v-else style="color:var(--gray-400);">-</span>
                                        </td>
                                        <td class="p-2">{{ c.group_name || '-' }}</td>
                                        <td class="p-2">
                                            <button @click="openLeadHistory(c)"
                                                class="text-xs px-2 py-1 rounded font-mono"
                                                :style="(c.lead_score || 0) > 5 ? 'background:var(--color-warning-bg);color:var(--color-warning);' : ((c.lead_score || 0) > 0 ? 'background:var(--gray-100);color:var(--gray-700);' : 'background:var(--gray-50);color:var(--gray-400);')"
                                            >{{ (c.lead_score || 0) }}</button>
                                        </td>
                                        <td class="p-2 text-sm" style="color:var(--gray-500);">{{ c.last_interaction_at ? new Date(c.last_interaction_at).toLocaleString() : '-' }}</td>
                                        <td class="p-2" v-if="isSuperAdmin || isDomainAdmin">
                                            <span class="font-mono text-xs">{{ c.user_id ? ('#' + c.user_id) : '-' }}</span>
                                        </td>
                                        <td class="p-2 text-sm" style="color:var(--gray-500);">{{ c.created_at ? new Date(c.created_at).toLocaleString() : '-' }}</td>
                                        <td class="p-2">
                                            <div class="flex gap-1">
                                                <button @click="openEditContact(c)" class="btn btn-ghost btn-xs" style="color:var(--brand-600);" title="編輯">編輯</button>
                                                <button @click="deleteContact(c)" class="btn btn-ghost btn-xs" style="color:var(--color-danger);" title="刪除">刪除</button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Pagination -->
                        <div v-if="contactsTotal > contactsLimit" class="flex justify-between items-center mt-4 pt-3 border-t" style="border-color:var(--gray-200);">
                            <span style="font-size:0.875rem;color:var(--gray-500);">
                                顯示第 {{ (contactsPage - 1) * contactsLimit + 1 }}–{{ Math.min(contactsPage * contactsLimit, contactsTotal) }} 筆，共 {{ contactsTotal }} 筆
                            </span>
                            <div class="flex gap-2">
                                <button @click="fetchContacts(contactsPage - 1)" :disabled="contactsPage <= 1" class="btn btn-secondary btn-sm">上一頁</button>
                                <span style="font-size:0.875rem;color:var(--gray-600);padding:0 0.5rem;line-height:2rem;">第 {{ contactsPage }} / {{ Math.ceil(contactsTotal / contactsLimit) }} 頁</span>
                                <button @click="fetchContacts(contactsPage + 1)" :disabled="contactsPage * contactsLimit >= contactsTotal" class="btn btn-secondary btn-sm">下一頁</button>
                            </div>
                        </div>
                    </div>

                    <div v-else class="card p-6">
                        <div v-if="contactGroupsLoading" class="p-6 text-center" style="color:var(--gray-500);">
                            <span class="animate-spin mr-2">⟳</span> 載入中...
                        </div>

                        <div v-else class="overflow-x-auto">
                            <table class="data-table">
                                <thead>
                                    <tr class="border-b" style="background:var(--gray-50);">
                                        <th class="p-2 w-16">ID</th>
                                        <th class="p-2">名稱</th>
                                        <th class="p-2">描述</th>
                                        <th class="p-2 w-44">建立時間</th>
                                        <th class="p-2 w-24" v-if="isSuperAdmin || isDomainAdmin">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="contactGroups.length === 0">
                                        <td :colspan="(isSuperAdmin || isDomainAdmin) ? 5 : 4" class="p-8 text-center" style="color:var(--gray-500);">
                                            尚無群組
                                        </td>
                                    </tr>
                                    <tr v-for="(g, idx) in safeArr(contactGroups)" :key="keyById(g, 'cg-row', idx)" class="border-b">
                                        <td class="p-2">{{ g.id }}</td>
                                        <td class="p-2 font-bold">{{ g.name }}</td>
                                        <td class="p-2">{{ g.description || '-' }}</td>
                                        <td class="p-2">{{ g.created_at ? new Date(g.created_at).toLocaleString() : '-' }}</td>
                                        <td class="p-2" v-if="isSuperAdmin || isDomainAdmin">
                                            <div class="flex gap-2">
                                                <button @click="openEditContactGroup(g)" class="btn btn-ghost btn-xs" style="color:var(--brand-600);">編輯</button>
                                                <button @click="deleteContactGroup(g)" class="btn btn-ghost btn-xs" style="color:var(--color-danger);">刪除</button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div v-if="showContactModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="card p-6 w-[640px] max-h-[90vh] overflow-y-auto">
                        <h3 class="card-title mb-4">{{ editingContactId ? '編輯聯絡人' : '新增聯絡人' }}</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm mb-1">姓名</label>
                                <input v-model="contactForm.name" class="input w-full" placeholder="姓名">
                            </div>
                            <div>
                                <label class="block text-sm mb-1">Email</label>
                                <input v-model="contactForm.email" class="w-full border p-2 rounded font-mono" placeholder="email@example.com">
                            </div>
                            <div>
                                <label class="block text-sm mb-1">電話</label>
                                <input v-model="contactForm.phone" class="input w-full" placeholder="選填">
                            </div>
                            <div>
                                <label class="block text-sm mb-1">標籤</label>
                                <!-- Tag chips display -->
                                <div class="flex flex-wrap gap-1 mb-1" v-if="contactForm.tags">
                                    <span v-for="(tag, ti) in contactForm.tags.split(',').map(t=>t.trim()).filter(Boolean)"
                                        :key="ti"
                                        style="display:inline-flex;align-items:center;gap:3px;background:var(--brand-100,#e0e7ff);color:var(--brand-700,#4338ca);border-radius:999px;padding:2px 10px 2px 10px;font-size:0.75rem;font-weight:600;">
                                        {{ tag }}
                                        <button type="button"
                                            @click="contactForm.tags = contactForm.tags.split(',').map(t=>t.trim()).filter((t,i)=>i!==ti).join(',')"
                                            style="background:none;border:none;cursor:pointer;color:inherit;padding:0;font-size:0.9rem;line-height:1;">&times;</button>
                                    </span>
                                </div>
                                <div style="display:flex;gap:4px;">
                                    <input ref="tagInputContact"
                                        @keydown.enter.prevent="e => { const v=(e.target.value||'').trim(); if(v){ contactForm.tags=(contactForm.tags?contactForm.tags+',':'')+v; e.target.value=''; } }"
                                        @keydown.comma.prevent="e => { const v=(e.target.value||'').trim(); if(v){ contactForm.tags=(contactForm.tags?contactForm.tags+',':'')+v; e.target.value=''; } }"
                                        class="input w-full text-sm" placeholder="輸入後按 Enter 新增，例如：VIP">
                                </div>
                            </div>
                            <div class="md:col-span-2">
                                <label class="block text-sm mb-1">備註</label>
                                <textarea v-model="contactForm.notes" class="input w-full" rows="4" placeholder="選填"></textarea>
                            </div>
                            <div v-if="isSuperAdmin || isDomainAdmin">
                                <label class="block text-sm mb-1">群組</label>
                                <select v-model="contactForm.group_id" class="input w-full">
                                    <option :value="null">不分群組</option>
                                    <option v-for="(g, idx) in safeArr(contactGroups)" :key="keyById(g, 'cg2', idx)" :value="g.id">{{ g.name }}</option>
                                </select>
                            </div>
                            <div v-if="isSuperAdmin || isDomainAdmin">
                                <label class="block text-sm mb-1">擁有者</label>
                                <select v-model="contactForm.user_id" class="input w-full">
                                    <option :value="null">共享（所有人可見）</option>
                                    <option v-for="(u, idx) in safeArr(users)" :key="keyById(u, 'user-opt', idx)" :value="u.id">{{ u.email }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="flex justify-end gap-2 mt-6">
                            <button @click="closeContactModal()" class="btn btn-outline btn-sm">取消</button>
                            <button @click="contactQuickFollowup" :disabled="contactFollowupGenerating || !contactForm.email"
                                class="btn btn-primary">
                                {{ contactFollowupGenerating ? '生成中...' : '一鍵智慧跟進' }}
                            </button>
                            <button @click="saveContact()" :disabled="contactSaving"
                                class="btn btn-primary">
                                {{ contactSaving ? '儲存中...' : '儲存' }}
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="showContactGroupModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="card p-6 w-[520px] max-h-[90vh] overflow-y-auto">
                        <h3 class="card-title mb-4">{{ editingContactGroupId ? '編輯群組' : '新增群組' }}</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm mb-1">名稱</label>
                                <input v-model="contactGroupForm.name" class="input w-full" placeholder="例如：VIP">
                            </div>
                            <div>
                                <label class="block text-sm mb-1">描述</label>
                                <textarea v-model="contactGroupForm.description" class="input w-full" rows="3" placeholder="選填"></textarea>
                            </div>
                        </div>
                        <div class="flex justify-end gap-2 mt-6">
                            <button @click="closeContactGroupModal()" class="btn btn-outline btn-sm">取消</button>
                            <button @click="saveContactGroup()" :disabled="contactGroupSaving"
                                class="btn btn-primary">
                                {{ contactGroupSaving ? '儲存中...' : '儲存' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Lead Score History Modal -->
                <div v-if="showLeadHistoryModal" class="modal-overlay">
                    <div class="modal-box w-full max-w-3xl">
                        <div class="flex justify-between items-center p-4 border-b rounded-t-lg" style="background:linear-gradient(to right,#334155,#0f172a);color:#fff;">
                            <h3 class="font-bold text-lg flex items-center gap-2">
                                📜 Lead Score 歷史
                                <span v-if="leadHistoryContact" style="font-size:0.875rem;font-weight:400;opacity:0.8;">{{ leadHistoryContact.name }} ({{ leadHistoryContact.email }})</span>
                            </h3>
                            <button @click="showLeadHistoryModal = false" class="btn btn-ghost btn-sm" style="color:#fff;">×</button>
                        </div>
                        <div class="p-6 overflow-y-auto" style="max-height:70vh;">
                            <div v-if="leadHistoryLoading" class="flex items-center justify-center py-10">
                                <span class="spinner spinner-sm" style="margin-right:0.5rem;"></span> 載入中...
                            </div>
                            <div v-else-if="safeArr(leadHistoryEvents).length === 0" class="p-8 text-center" style="color:var(--gray-500);">
                                尚無歷史紀錄
                            </div>
                            <div v-else class="space-y-3">
                                <div v-for="(e, idx) in safeArr(leadHistoryEvents)" :key="keyById(e, 'lhe', idx)"
                                    class="rounded p-4 border" style="background:var(--surface);border-color:var(--gray-200);">
                                    <div class="flex flex-wrap gap-3 items-center justify-between">
                                        <div class="flex items-center gap-2">
                                            <span class="text-xs px-2 py-1 rounded font-mono"
                                                :class="(e.score_value || 0) > 5 ? 'badge badge-warning' : ((e.score_value || 0) > 0 ? 'badge badge-default' : 'badge')"
                                            >{{ e.score_label || '-' }}{{ e.score_value !== null && e.score_value !== undefined ? (' (' + e.score_value + ')') : '' }}</span>
                                            <span style="font-size:0.75rem;color:var(--gray-500);font-family:monospace;">{{ e.created_at ? new Date(e.created_at).toLocaleString() : '-' }}</span>
                                        </div>
                                        <div style="font-size:0.75rem;color:var(--gray-500);font-family:monospace;" v-if="e.subject">{{ e.subject }}</div>
                                    </div>
                                    <div v-if="e.action" class="mt-3 rounded p-3 text-sm" style="background:var(--brand-50,#eef2ff);border:1px solid var(--brand-100,#e0e7ff);color:var(--brand-900,#1e1b4b);">
                                        <span class="font-bold">建議行動：</span>{{ e.action }}
                                    </div>
                                    <div v-if="e.reasoning" class="mt-3 rounded p-3 text-sm whitespace-pre-wrap" style="background:var(--gray-50);border:1px solid var(--gray-200);color:var(--gray-800);">{{ e.reasoning }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button @click="showLeadHistoryModal = false" class="btn btn-secondary">關閉</button>
                        </div>
                    </div>
                </div>

                <!-- CSV Import Modal -->
                <div v-if="showContactImportModal" class="modal-overlay" style="z-index:9999;">
                    <div class="modal-box w-full max-w-2xl">
                        <div class="modal-header">
                            <h3 class="modal-title">⬆ CSV 批量匯入聯絡人</h3>
                            <button @click="showContactImportModal = false" class="btn btn-ghost btn-sm">×</button>
                        </div>
                        <div class="modal-body space-y-4">
                            <div class="text-sm p-3" style="background:var(--brand-50);border:1px solid var(--brand-200);border-radius:var(--radius-md);color:var(--gray-700);">
                                CSV 必須包含 <strong>email</strong> 欄（必填）、<strong>name</strong>、<strong>phone</strong>、<strong>notes</strong>、<strong>tags</strong>（皆選填）。第一行為欄位標題。
                                <br>範例：<code>name,email,phone,tags</code>
                            </div>

                            <div v-if="contactImportPreview.length > 0">
                                <div class="flex items-center justify-between mb-2">
                                    <span style="font-size:0.875rem;font-weight:700;color:var(--gray-700);">
                                        已解析 {{ contactImportPreview.length }} 筆（預覽前 5 筆）
                                    </span>
                                </div>
                                <div class="overflow-x-auto">
                                    <table class="data-table">
                                        <thead>
                                            <tr>
                                                <th>姓名</th>
                                                <th>Email</th>
                                                <th>電話</th>
                                                <th>標籤</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(c, idx) in contactImportPreview.slice(0, 5)" :key="idx">
                                                <td>{{ c.name }}</td>
                                                <td>{{ c.email }}</td>
                                                <td>{{ c.phone || '—' }}</td>
                                                <td>{{ c.tags || '—' }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-if="contactImportPreview.length > 5" style="font-size:0.75rem;color:var(--gray-400);margin-top:0.375rem;">
                                    ... 及其餘 {{ contactImportPreview.length - 5 }} 筆
                                </div>
                            </div>

                            <div v-if="contactImportResult" class="text-sm p-3"
                                style="background:var(--color-success-bg);border:1px solid #a7f3d0;border-radius:var(--radius-md);color:#065f46;">
                                匯入完成：{{ contactImportResult.imported }} 筆成功，{{ contactImportResult.skipped }} 筆略過（重複）
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button @click="showContactImportModal = false" class="btn btn-ghost btn-sm">取消</button>
                            <button @click="importContactsFromCsv(contactImportPreview)"
                                :disabled="contactImportLoading || contactImportPreview.length === 0"
                                class="btn btn-primary">
                                <span v-if="contactImportLoading" class="spinner spinner-sm"></span>
                                {{ contactImportLoading ? '匯入中...' : `確認匯入 ${contactImportPreview.length} 筆` }}
                            </button>
                        </div>
                    </div>
                </div>

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

const {
  currentView, isSuperAdmin, isDomainAdmin,
  contacts, contactsTotal, contactsPage, contactsLimit,
  contactForm, contactsTab, contactFilters,
  contactGroups, contactGroupsLoading, contactGroupSaving, contactGroupForm,
  editingContactId, showContactModal, showContactGroupModal, editingContactGroupId,
  contactTags, contactsLoading, contactSaving, contactFollowupGenerating,
  showContactImportModal, contactImportPreview, contactImportLoading, contactImportResult,
  showLeadHistoryModal, leadHistoryLoading, leadHistoryContact, leadHistoryEvents,
  users,
} = storeToRefs(store)

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  safeArr, keyById,
  fetchContacts, saveContact, deleteContact,
  canEditContact, openEditContact, openLeadHistory,
  openCreateContactGroup, openEditContactGroup, saveContactGroup, deleteContactGroup,
  fetchContactGroups,
  openCreateContact, closeContactModal, closeContactGroupModal,
  contactQuickFollowup, resetContactFilters,
  parseCsvToContacts, importContactsFromCsv,
} = store

function handleContactCsvUpload(e) {
  const file = e.target.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (ev) => {
    const result = parseCsvToContacts(ev.target.result)
    if (result === null) {
      window.$toast?.error('CSV 缺少必要的 email 欄，請檢查標題行')
      return
    }
    if (result.length === 0) {
      window.$toast?.error('CSV 未解析到任何有效聯絡人')
      return
    }
    store.contactImportPreview = result
    store.contactImportResult = null
    store.showContactImportModal = true
  }
  reader.readAsText(file, 'UTF-8')
  e.target.value = ''
}
</script>

